import { useEffect, useRef } from 'react';

interface JsonLdProps {
  data: Record<string, unknown>;
}

/**
 * Injects a JSON-LD script tag into the document head.
 * Removes it on unmount to avoid duplicates during navigation.
 */
export function JsonLd({ data }: JsonLdProps) {
  const scriptRef = useRef<HTMLScriptElement | null>(null);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
    scriptRef.current = script;

    return () => {
      if (scriptRef.current && scriptRef.current.parentNode) {
        scriptRef.current.parentNode.removeChild(scriptRef.current);
      }
    };
  }, [JSON.stringify(data)]);

  return null;
}

/** Helper to build a BlogPosting JSON-LD object */
export function blogPostingJsonLd(post: {
  title: string;
  excerpt: string;
  author: string;
  date: string;
  slug: string;
  category: string;
  readTime: string;
  content: { paragraphs: string[] }[];
}) {
  const isoDate = new Date(post.date).toISOString();
  const wordCount = post.content.reduce(
    (acc, section) => acc + section.paragraphs.join(' ').split(/\s+/).length,
    0,
  );

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    author: {
      '@type': 'Person',
      name: post.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'LogPulse',
      url: 'https://logpulse.io',
      logo: {
        '@type': 'ImageObject',
        url: 'https://logpulse.io/favicon.svg',
      },
    },
    datePublished: isoDate,
    dateModified: isoDate,
    url: `https://logpulse.io/blog/${post.slug}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://logpulse.io/blog/${post.slug}`,
    },
    articleSection: post.category,
    wordCount,
    inLanguage: 'en-US',
    isAccessibleForFree: true,
    image: 'https://logpulse.io/og-image.jpg',
  };
}

/** Helper to build a BreadcrumbList JSON-LD object */
export function breadcrumbJsonLd(
  items: { name: string; url: string }[],
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}
